import React from 'react';
import { Tabs, Typography, Button, Modal, Input, message, Tooltip } from 'antd';
import { Banner } from '../../../types/banner';
import { MediaPreview } from './media-preview';
import { CameraOutlined, VideoCameraOutlined, MailOutlined, DownloadOutlined } from '@ant-design/icons';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { addCacheBusting, getAllImages, getAllVideos } from '../../../utils';
import { useDownloadMedia } from '../../../hooks/media/use-download-media';
import { useSendMediaEmail } from '../../../hooks/media/use-send-media';
import { FileDown } from 'lucide-react';

const { Text } = Typography;

const MEDIA_WRAPPER_STYLE: React.CSSProperties = {
  background: '#f8fafc',
  borderTopLeftRadius: 16,
  borderBottomLeftRadius: 16,
  border: 'none',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden'
};

interface AssetMediaPanelProps {
  banner: Banner;
}

export const AssetMediaPanel: React.FC<AssetMediaPanelProps> = ({ banner }) => {
  const { screens, fontSize, spacing } = useResponsiveSizes();
  const { downloadBannerZip } = useDownloadMedia();
  const { sendMediaEmail } = useSendMediaEmail();

  const videos = getAllVideos(banner);
  const images = getAllImages(banner);

  const getInitialTab = (): 'video' | 'image' => {
    const hasVideos = videos.length > 0;
    const hasImages = images.length > 0;

    if (hasVideos) return 'video';
    if (hasImages) return 'image';

    return 'image';
  };

  const [activeTab, setActiveTab] = React.useState<'video' | 'image'>(getInitialTab());
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [emailError, setEmailError] = React.useState('');

  const videoUrlsAndFormats = videos.map(video => ({
    url: addCacheBusting(video.url, banner.updatedAt),
    format: video.format
  }));
  const imageUrlsAndFormats = images.map(img => ({
    url: addCacheBusting(img.url, banner.updatedAt),
    format: img.format
  }));

  const hasVideo = videoUrlsAndFormats.length > 0;
  const hasImage = imageUrlsAndFormats.length > 0;
  const hasContent = hasVideo || hasImage;

  const handleDownloadAll = async () => {
    if (!banner.bannerId) return;

    setIsDownloading(true);
    try {
      await downloadBannerZip(banner.bannerId);
      message.success('Descarga iniciada');
    } catch (error) {
      console.error('Error al generar descarga:', error);
      message.error('Error al generar la descarga');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!banner.bannerId) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailInput.trim()) {
      setEmailError('Por favor ingresa un email');
      return;
    }
    if (!emailRegex.test(emailInput)) {
      setEmailError('Email inválido');
      return;
    }

    setIsSendingEmail(true);
    setIsEmailModalOpen(false);

    try {
      await sendMediaEmail(banner.bannerId, emailInput.trim());
      message.success('Email enviado correctamente');
      setEmailInput('');
    } catch (error) {
      console.error('Error al enviar el email:', error);
      message.error('Error al enviar el email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleOpenEmailModal = () => {
    setEmailInput('');
    setEmailError('');
    setIsEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmailInput('');
    setEmailError('');
  };

  if (!hasContent) {
    return (
      <div
        style={{
          ...MEDIA_WRAPPER_STYLE,
          color: '#64748b',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Text style={{ color: '#64748b', fontSize: fontSize.base }}>No hay contenido disponible para mostrar</Text>
      </div>
    );
  }

  const getScrollStyle = (count: number): React.CSSProperties =>
    count > 1
      ? {
          flex: 1,
          maxHeight: '50vh',
          overflowY: 'auto',
          //scrollbarWidth: 'thin',
          scrollbarColor: '#e3e3e3 #FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: `${spacing.md}px ${spacing.xs}px`
        }
      : {
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: `${spacing.md}px ${spacing.xs}px`
        };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: spacing.md }}>
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'video' | 'image')}
        size='small'
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        tabBarExtraContent={
          <div style={{ padding: '0 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button
              type='primary'
              size={screens.xs ? 'small' : screens.lg ? 'middle' : 'small'}
              icon={<DownloadOutlined />}
              onClick={handleDownloadAll}
              loading={isDownloading}
              disabled={isDownloading}
            />

            <Button
              type='primary'
              size={screens.xs ? 'small' : screens.lg ? 'middle' : 'small'}
              icon={<MailOutlined />}
              onClick={handleOpenEmailModal}
              loading={isSendingEmail}
              disabled={isSendingEmail}
            />

            {hasVideo && activeTab === 'video' && (
              <Tooltip title='Pendiente configuración'>
                <Button icon={<FileDown width={16} />} disabled>
                  VAST
                </Button>
              </Tooltip>
            )}
          </div>
        }
        items={[
          {
            key: 'video',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  borderRadius: 10,
                  fontSize: fontSize.base,
                  fontWeight: 600
                }}
              >
                <VideoCameraOutlined /> Video ({videoUrlsAndFormats.length})
              </div>
            ),
            disabled: !hasVideo,
            children: hasVideo ? (
              <div style={MEDIA_WRAPPER_STYLE}>
                <div className='media-scroll' style={getScrollStyle(videoUrlsAndFormats.length)}>
                  {videoUrlsAndFormats.map((item, idx) => (
                    <MediaPreview key={idx} type='video' src={item.url} format={item.format} />
                  ))}
                </div>
              </div>
            ) : null
          },
          {
            key: 'image',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  borderRadius: 10,
                  fontSize: fontSize.base,
                  fontWeight: 600
                }}
              >
                <CameraOutlined /> Imagen ({imageUrlsAndFormats.length})
              </div>
            ),
            disabled: !hasImage,
            children: hasImage ? (
              <div style={MEDIA_WRAPPER_STYLE}>
                <div className='media-scroll' style={getScrollStyle(imageUrlsAndFormats.length)}>
                  {imageUrlsAndFormats.map((item, idx) => (
                    <MediaPreview key={idx} type='image' src={item.url} format={item.format} />
                  ))}
                </div>
              </div>
            ) : null
          }
        ]}
      />

      <Modal
        title='Enviar asset por email'
        open={isEmailModalOpen}
        onOk={handleSendEmail}
        onCancel={handleCloseEmailModal}
        okText='Enviar'
        cancelText='Cancelar'
        confirmLoading={isSendingEmail}
        width={400}
      >
        <div style={{ padding: '10px 0' }}>
          <Text style={{ marginBottom: 8, display: 'block' }}>Ingresa el email donde queres recibir tu asset</Text>
          <Input
            placeholder='user@digitaltrendinglab.com'
            value={emailInput}
            onChange={e => {
              setEmailInput(e.target.value);
              setEmailError('');
            }}
            onPressEnter={handleSendEmail}
            status={emailError ? 'error' : ''}
            size='small'
          />
          {emailError && (
            <Text type='danger' style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {emailError}
            </Text>
          )}
        </div>
      </Modal>
    </div>
  );
};
